<template>
  <section v-if="!isReady || isGetCollectionRequestPending" class="prompts">
    <p-loading />
  </section>

  <section v-else class="prompts">
    <header>
      <h1>Content Chains</h1>
      <section class="action-wrapper">
        <router-link v-if="$hasPermission('prompts.write')" to="/chains/create">+ new</router-link>
      </section>
    </header>

    <section>
      <p-list
        name="prompts"
        :data="filteredCollection"
        :total="filteredCollection.length !== total ? `${filteredCollection.length} / ${total}` : ` ${total}`"
        :metadata="{
          name: { label: 'Name', width: 'minmax(0, 3fr)' },
          description: { label: 'Description', width: 'minmax(0, 3fr)' },
          tags: { label: 'Tags', width: 'minmax(100px, 2fr)' },
          organizations: { label: 'Organizations', width: 'minmax(100px, 2fr)', labelCallback: getOrganizationName },
          categories: { label: 'Categories', width: 'minmax(100px, 2fr)', labelCallback: getCategoryName },
          isGeneric: { label: 'Generic', width: '60px' },
          isPublic: { label: 'Public', width: '60px' },
          isContentChain: { label: 'Content Chain', width: '100px' },
          modifiedAt: { label: 'Last modification', width: '175px' }
        }"
        @edit="edit"
      >
        <template #name="{ value, item }">
          <router-link :to="`/chains/${item.id}`">{{ value }}</router-link>
        </template>
        <template #__actions="{ item }">
          <p-button variant="text" title="Edit this content chain" @click="edit(item.id)"> &#9998;</p-button>
        </template>
        <template slot="filter">
          <div class="tags-wrapper">
            <div class="checkbox-wrapper">
              <Checkbox v-model="isGeneric" label="Generic only" />
              <Checkbox v-model="isContentChain" label="Content Chains only" />
              <Checkbox v-model="isPublished" label="Published only" />
            </div>
            <Multiselect
              v-model="categories"
              :multiple="true"
              placeholder="Filter by category..."
              :options="categoriesOptions"
              :get-label-callback="getCategoryName"
            />
            <Multiselect v-model="tags" :multiple="true" placeholder="Filter by tag..." :options="allTags" :options-limit="100" />
            <Multiselect v-model="organizations" :multiple="true" placeholder="Filter by organization..." :options="organizationsOptions" />
            <Multiselect
              v-model="models"
              :multiple="true"
              placeholder="Filter by model..."
              :options="modelOptions"
              :get-label-callback="getModelName"
            />
            <Multiselect v-model="providers" :multiple="true" placeholder="Filter by provider..." :options="providerOptions" />
          </div>
        </template>
      </p-list>
    </section>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import Loading from '@/components/common/Loading';
import Button from '@/components/common/Button';

import List from '@/components/common/List';
import Multiselect from '@/components/common/Multiselect';
import Checkbox from '@/components/common/Checkbox.vue';

export default {
  components: {
    'p-list': List,
    'p-loading': Loading,
    'p-button': Button,
    Multiselect,
    Checkbox
  },
  data() {
    return {
      isReady: false,
      tags: [],
      organizations: [],
      categories: [],
      models: [],
      providers: [],
      isGeneric: false,
      isContentChain: false,
      isPublished: false
    };
  },
  computed: {
    ...mapState({
      collection: s => s.prompts.collection,
      total: s => s.prompts.total || s.prompts.collection.length,
      isGetCollectionRequestPending: s => s.prompts.isGetCollectionRequestPending,
      organizationsCollection: s => s.organizations.collection,
      categoriesCollection: state => state.library.categories,
      modelsCollection: state => state.nlp.models ?? [],
      providersCollection: state => state.nlp.providers ?? []
    }),
    allTags() {
      const set = this.collection.reduce((acc, item) => {
        item.tags && item.tags.forEach(element => acc.add(element));

        return acc;
      }, new Set());
      return Array.from(set);
    },
    organizationsOptions() {
      return this.organizationsCollection.map(org => org.name);
    },
    categoriesOptions() {
      return this.categoriesCollection.map(cat => cat.id);
    },
    modelOptions() {
      const set = this.modelsCollection.reduce((acc, model) => {
        acc.add(model.id);
        return acc;
      }, new Set());

      this.collection.forEach(item => {
        (item.models ?? [])
          .map(model => this.normalizeModelId(model))
          .filter(Boolean)
          .forEach(model => set.add(model));
      });

      if (this.collection.some(item => item.hasMissingModels)) {
        set.add('missing');
      }

      return Array.from(set).sort((left, right) => this.getModelName(left).localeCompare(this.getModelName(right)));
    },
    providerOptions() {
      const set = this.providersCollection.reduce((acc, provider) => {
        provider.name && acc.add(provider.name);
        return acc;
      }, new Set());

      this.collection.forEach(item => {
        this.getItemProviders(item)
          .filter(Boolean)
          .forEach(provider => set.add(provider));
      });

      return Array.from(set).sort((left, right) => left.localeCompare(right));
    },
    organizationsMapping() {
      return this.organizationsCollection.reduce((acc, org) => {
        acc[org.id] = org.name;
        return acc;
      }, {});
    },
    filteredCollection() {
      return this.collection.filter(item => {
        if (this.organizations?.length > 0) {
          if (!item.organizations) {
            return false;
          }
          if (item.organizations.length === 0) {
            return false;
          }
          const orgNames = item.organizations.map(id => this.organizationsMapping[id]);
          const organizationsIntersecting = this.organizations.some(org => orgNames.includes(org));
          if (!organizationsIntersecting) {
            return false;
          }
        }
        if (this.tags.length !== 0) {
          if (!item.tags?.some(tag => this.tags.includes(tag))) {
            return false;
          }
        }

        if (this.categories.length !== 0) {
          const hasCategory = this.categories.some(category => item.categories?.includes(category));
          if (!hasCategory) {
            return false;
          }
        }
        if (this.models.length !== 0) {
          const itemModels = this.getItemModels(item);
          const hasModel = this.models.some(model => {
            if (model === 'missing') {
              return item.hasMissingModels;
            }

            return itemModels.includes(model);
          });
          if (!hasModel) {
            return false;
          }
        }
        if (this.providers.length !== 0) {
          const itemProviders = this.getItemProviders(item);
          const hasProvider = this.providers.some(provider => itemProviders.includes(provider));
          if (!hasProvider) {
            return false;
          }
        }
        if (this.isGeneric && !item.isGeneric) {
          return false;
        }
        if (this.isContentChain && !item.isContentChain) {
          return false;
        }

        if (this.isPublished && !item.isPublic) {
          return false;
        }

        return true;
      });
    }
  },

  async created() {
    await Promise.all([
      this.$store.dispatch('prompts/getCollection'),
      this.$store.dispatch('organizations/getCollection'),
      this.$store.dispatch('library/getCategories'),
      this.$store.dispatch('nlp/getConfigurations')
    ]);
    this.isReady = true;
  },
  methods: {
    async edit(id) {
      this.$router.push({ path: `/chains/${id}/edit` });
    },
    getOrganizationName(id) {
      return this.organizationsCollection.find(o => o.id === id)?.name ?? 'Unknown Organization';
    },
    getCategoryName(value) {
      const option = this.categoriesCollection.find(o => o.id === value);
      if (!option) {
        return 'Unknown Category';
      }
      if (option.parentId) {
        const parent = this.categoriesCollection.find(o => o.id === option.parentId);
        if (!parent) {
          return option.name;
        }
        return `${parent.name} > ${option.name}`;
      }
      return option.name;
    },
    getModelName(id) {
      if (id === 'missing') {
        return 'Model missing';
      }

      return this.modelsCollection.find(model => model.id === id)?.name ?? `Unavailable model (${id})`;
    },
    normalizeModelId(candidate) {
      if (!candidate) {
        return null;
      }

      const direct = this.modelsCollection.find(model => model.id === candidate);
      if (direct) {
        return direct.id;
      }

      for (const model of this.modelsCollection) {
        if (!model.config) {
          continue;
        }

        try {
          const config = JSON.parse(model.config);
          if (config.deployment === candidate) {
            return model.id;
          }
        } catch {
          continue;
        }
      }

      return candidate;
    },
    getItemModels(item) {
      return Array.from(new Set((item.models ?? []).map(model => this.normalizeModelId(model)).filter(Boolean)));
    },
    getItemProviders(item) {
      const providers = item.providers?.length ? [...item.providers] : [];
      let hasUnavailableModel = false;

      this.getItemModels(item).forEach(modelId => {
        const provider = this.modelsCollection.find(model => model.id === modelId)?.provider;
        if (provider) {
          providers.push(provider);
        } else {
          hasUnavailableModel = true;
        }
      });

      if (hasUnavailableModel) {
        providers.push('Unknown provider');
      }

      return Array.from(new Set(providers));
    }
  }
};
</script>

<style scoped lang="scss">
.prompts {
  display: grid;
  padding: 0 0.5rem 0 1rem;
  grid-template-columns: 1fr;
  grid-template-rows: max-content minmax(0, 1fr);
  grid-gap: 0.5rem;
  width: 100%;
  height: 100%;
  .tags-wrapper {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    grid-gap: 5px;
    gap: 5px;
    .checkbox-wrapper {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 4px;
      white-space: nowrap;
    }
    .multiselect-wrapper {
      width: 200px;
      flex: 0 0 200px;
    }
  }
  > header {
    padding: 0.5rem 0.5rem 0;
    display: flex;
    justify-content: space-between;

    box-sizing: border-box;

    h1 {
      color: var(--theme-on-surface);
      font-size: 1.5rem;
      font-weight: 700;
      margin-right: 0.25rem;
    }

    .action-wrapper {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
  }
}
</style>
